import { useEffect, useRef } from 'react';

/**
 * Scroll-driven hanging effect with top-right pivot.
 * Adds translateY + rotate + skew + scale as the element crosses viewport.
 */
const useHangingParallax = (options = {}) => {
  // Defaults match the provided extremes:
  // Max up:   translateY -38px, rotate +2deg
  // Max down: translateY +8px,  rotate -5deg
  const {
    translateY = { up: -38, down: 8 },
    rotate = { upDeg: 2, downDeg: -5 },
    skew = { upDeg: 0, downDeg: 0 },
    scale = { up: 1, down: 1 },
    ease = 0.07,
  } = options;

  const ref = useRef(null);
  const rafRef = useRef(0);
  const currentY = useRef(0);
  const targetY = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.style.transformOrigin = 'top right';

    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const inView = rect.top < vh && rect.bottom > 0;
      if (!inView) return;

      // progress relative to viewport center -> [-1, 1]
      const center = rect.top + rect.height / 2;
      const progress = (vh / 2 - center) / (vh / 2 + rect.height / 2);
      const p = Math.max(-1, Math.min(1, progress));
      const t = (p + 1) / 2; // 0..1, where 0=up, 1=down
      const yUp = translateY.up ?? -38;
      const yDown = translateY.down ?? 8;
      targetY.current = yUp + (yDown - yUp) * t; // lerp
      if (!rafRef.current) rafRef.current = requestAnimationFrame(update);
    };

    const update = () => {
  currentY.current += (targetY.current - currentY.current) * ease;
  const yUp = translateY.up ?? -38;
  const yDown = translateY.down ?? 8;
  const t = (currentY.current - yUp) / (yDown - yUp || 1); // 0..1 across up->down
  const rotUp = rotate.upDeg ?? 2;
  const rotDown = rotate.downDeg ?? -5;
  const rot = rotUp + (rotDown - rotUp) * t;
  const skUp = skew.upDeg ?? 0;
  const skDown = skew.downDeg ?? 0;
  const sk = skUp + (skDown - skUp) * t;
  const scUp = scale.up ?? 1;
  const scDown = scale.down ?? 1;
  const sc = scUp + (scDown - scUp) * t;

      el.style.transform = `translateY(${currentY.current.toFixed(2)}px) rotate(${rot.toFixed(2)}deg) skewY(${sk.toFixed(2)}deg) scale(${sc.toFixed(3)})`;

      if (Math.abs(targetY.current - currentY.current) > 0.1) {
        rafRef.current = requestAnimationFrame(update);
      } else {
        rafRef.current = 0;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ease, translateY.up, translateY.down, rotate.upDeg, rotate.downDeg, skew.upDeg, skew.downDeg, scale.up, scale.down]);

  return { ref };
};

export default useHangingParallax;
